import React, { useContext, useEffect, useRef, useState } from "react";
import { Route, useLocation } from "react-router-dom";

import { ApplicationContextProvider } from "@swa-ui/application";
import { ThemeProvider } from "@swa-ui/design-tokens";
import { DotcomPageLayout } from "@swa-ui/dotcom";
import {
  FlightDataContext,
  FlightDataProvider,
  InflightPageFooter,
  InflightWifiHeader,
  WifiAuthenticationContext,
  wifiAuthStates,
} from "@swa-ui/inflight";
import { PlacementsContext } from "@swa-ui/placements";

import { HomePage } from "../../pages/HomePage";
import { InflightUserStatusContext } from "../../providers";
import { ErrorPage } from "../ErrorPage";
import { LoadingShimmer } from "../LoadingShimmer";

const basePath = "/inflight/home-page";
const PARAMETER_PROVIDER_KEY = "pageContext";
const CHARTER_APP_CONTEXT = ["charter"];

export const InflightHomePageContainer = () => {
  const {
    error: placementsError,
    loading: placementsLoading,
    loadPlacements,
    registerParameterProvider,
    setPlacementsAppContext,
    updatePlacementsConfig,
  } = useContext(PlacementsContext);
  const { wifiAuthenticationState } = useContext(WifiAuthenticationContext);
  const { error, loading, sandboxStatus } = useContext(InflightUserStatusContext);
  const { pathname } = useLocation();
  const [showContent, setShowContent] = useState(false);
  const previousAuthState = useRef(null);
  const placementsHaveLoaded = useRef(false);
  const flightDataInitialized = useRef(false);

  useEffect(() => {
    registerParameterProvider(PARAMETER_PROVIDER_KEY, () => ({
      loginState: wifiAuthenticationState,
    }));

    if (previousAuthState.current && previousAuthState.current !== wifiAuthenticationState) {
      setShowContent(false);
      loadPlacements(pathname);
    }

    previousAuthState.current = wifiAuthenticationState;
  }, [loadPlacements, pathname, registerParameterProvider, wifiAuthenticationState]);

  useEffect(() => {
    if (placementsLoading?.current?.pathname) {
      placementsHaveLoaded.current = false;
    } else if (!placementsHaveLoaded.current && placementsLoading?.last?.pathname) {
      placementsHaveLoaded.current = true;
      revealIfReady();
    }
  }, [placementsLoading]);

  useEffect(() => {
    if (placementsError) {
      placementsHaveLoaded.current = true;
      revealIfReady();
    }
  }, [placementsError]);

  return error ? (
    <ThemeProvider themeName="customer">
      <ErrorPage />
    </ThemeProvider>
  ) : (
    <ApplicationContextProvider
      context={{ footer: <InflightPageFooter />, header: <InflightWifiHeader /> }}
    >
      <DotcomPageLayout>
        {loading || isUnsandboxPending() ? (
          <LoadingShimmer />
        ) : (
          <FlightDataProvider sandboxStatus={sandboxStatus}>
            <InflightHomePageContent onInitialized={handleInitialized} showContent={showContent} />
          </FlightDataProvider>
        )}
      </DotcomPageLayout>
    </ApplicationContextProvider>
  );

  function isUnsandboxPending() {
    return Boolean(
      sandboxStatus?.clientId &&
        sandboxStatus?.sandboxed &&
        wifiAuthenticationState === wifiAuthStates.FULL_AUTH
    );
  }

  function handleInitialized(flightData) {
    flightDataInitialized.current = true;

    if (flightData?.is_charter) {
      setPlacementsAppContext({ charterAppContext: CHARTER_APP_CONTEXT });
    }

    updatePlacementsConfig({
      pageContext: { flightType: flightData?.is_charter ? "charter" : "standard" },
    });

    revealIfReady();
  }

  function revealIfReady() {
    if (flightDataInitialized.current && placementsHaveLoaded.current) {
      setShowContent(true);
    }
  }
};

/**
 * Child component that accesses FlightDataContext (available only inside FlightDataProvider)
 * and notifies the parent when the flight data fetch has completed.
 *
 * Note: flightDataInitialized is not reset on auth state changes because flight data is
 * auth-independent — it contains flight info (departure, arrival, tail number) that does not
 * vary by passenger auth state.
 */
const InflightHomePageContent = ({ onInitialized, showContent }) => {
  const flightDataContext = useContext(FlightDataContext);
  const initialized = flightDataContext?.initialized ?? false;
  const hasNotified = useRef(false);

  useEffect(() => {
    if (initialized && !hasNotified.current) {
      hasNotified.current = true;
      onInitialized(flightDataContext?.flightData);
    }
  }, [initialized]);

  return (
    <>
      {!showContent && <LoadingShimmer />}
      {showContent && (
        <>
          <Route path={`${basePath}/`} exact>
            <HomePage />
          </Route>
          <Route path={`/`} exact>
            <HomePage />
          </Route>
        </>
      )}
    </>
  );
};
